import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    Injector,
    linkedSignal,
    signal,
    untracked,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { PricingStore } from "../s25.event.pricing.store";
import { lineItemType, Occurrence } from "../../../../pojo/Pricing";
import { S25Util } from "../../../../util/s25-util";
import { S25CheckboxModule } from "../../../s25-checkbox/s25.checkbox.module";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { Bind } from "../../../../decorators/bind.decorator";
import { WSReservations } from "../../../s25-event/EventMicroI";
import { OccDataMap, S25PricingUtil } from "../../s25.pricing.util";
import { S25IconComponent } from "../../../s25-icon/s25.icon.component";
import { S25FadingSuccessMsgComponent } from "../../../s25-fading-success-msg/s25.fading.success.msg.component";
import { InvoiceForm } from "../invoice/invoice-form/invoice-form";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";

type IncludedOccurrence = WSReservations & { rsrvId: number; eventId: number | undefined };

@TypeManagerDecorator("s25-ng-included-occurrences")
@Component({
    selector: "s25-ng-included-occurrences",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25CheckboxModule, S25ButtonComponent, S25IconComponent, S25FadingSuccessMsgComponent],
    template: `
        <div class="io-head">
            <h3 class="io-label">Included Occurrences</h3>
            <div class="io-toolbar">
                <span class="io-status">
                    @if (editing()) {
                        <strong>{{ selectedCount() }} selected</strong>
                        <span class="io-hint">Check to add, uncheck to remove</span>
                    } @else {
                        <strong>{{ includedCountDisplay() }}</strong>
                        @if (dateRangeDisplay()) {
                            <span class="io-range">· {{ dateRangeDisplay() }}</span>
                        }
                    }
                </span>
                @if (canEditOccurrences()) {
                    <div class="io-actions">
                        @if (editing()) {
                            <button type="button" class="aw-button aw-button--outline" (click)="cancel()">
                                Cancel
                            </button>
                            <s25-ng-button
                                [type]="'primary'"
                                [onClick]="save"
                                [disabled]="saveDisabled()"
                                [disabledReason]="'No changes to save'"
                            >
                                Save
                            </s25-ng-button>
                        } @else {
                            <s25-ng-button
                                [type]="'primary'"
                                [buttonClass]="'io-edit'"
                                [ariaLabel]="'Edit included occurrences'"
                                [disabled]="billsByDateRange()"
                                [disabledReason]="editDisabledReason()"
                                (clicked)="startEditing()"
                            >
                                Edit
                            </s25-ng-button>
                        }
                    </div>
                }
            </div>
        </div>
        <div class="io-divider" aria-hidden="true"></div>

        @if (error()) {
            <div class="cn-alert cn-alert--danger" role="alert">{{ error() }}</div>
        }
        @if (successMsg()) {
            <s25-ng-fading-success-msg class="io-success" [(message)]="successMsg" />
        }

        <ul class="io-list" [attr.aria-label]="editing() ? 'Occurrences to include' : 'Included occurrences'">
            @for (occ of displayedOccurrences(); track occ.rsrvId) {
                <li>
                    @if (editing()) {
                        <s25-ng-checkbox
                            [value]="pendingIncludedIds().has(occ.rsrvId)"
                            (valueChange)="toggleOccurrence(occ.rsrvId, $event)"
                        >
                            {{ occ.occurrence }}
                        </s25-ng-checkbox>
                    } @else {
                        <s25-ng-icon [type]="'calendarCalendar'" [ariaLabel]="false" />
                        {{ occ.occurrence }}
                    }
                </li>
            } @empty {
                <li class="io-empty">No occurrences found</li>
            }
        </ul>
    `,
    styles: `
        :host {
            display: block;
        }

        .io-head {
            padding: 20px 22px;
        }

        .io-label {
            margin: 0;
            font-size: 11px;
            font-weight: 400;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .io-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            margin-top: 6px;
            flex-wrap: wrap;
        }

        .io-status {
            display: flex;
            align-items: baseline;
            gap: 8px;
        }

        .io-hint,
        .io-range {
            font-size: 14px;
            color: var(--color--text-muted);
        }

        .io-actions {
            display: flex;
            gap: 8px;
        }

        .io-divider {
            height: 1px;
            background: var(--color--border);
        }

        .io-list {
            list-style: none;
            padding: 16px 22px;
            margin: 0;
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            row-gap: 6px;
            column-gap: 2em;
            max-height: calc(5 * 1.75em + 4 * 6px + 32px);
            overflow-x: auto;
        }

        .io-list li {
            width: max-content;
            display: flex;
            align-items: center;
            gap: 5px;
        }

        .io-empty {
            color: var(--color--text-muted);
            font-style: italic;
        }

        .cn-alert.cn-alert--danger {
            margin-block: 1rem 0;
            font-size: 0.9rem;
        }

        .io-success {
            display: block;
            margin: 1rem 22px 0;
        }
    `,
})
export class S25IncludedOccurrencesComponent {
    private readonly pricingStore = inject(PricingStore);
    private readonly injector = inject(Injector);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    readonly eventId = this.pricingStore.eventId;
    readonly selectedInvoice = this.pricingStore.selectedInvoice;
    readonly canEdit = this.pricingStore.canEditPricing;
    readonly combineRelated = this.pricingStore.combineRelated;

    private readonly allOccurrences = this.pricingStore.occurrences;
    private readonly allInvoices = this.pricingStore.invoices;
    private readonly dateFormats = this.pricingStore.dateFormats;

    readonly invoiceLocked = computed(() => !!this.selectedInvoice()?.billDefn?.completed);
    readonly canEditOccurrences = computed(() => this.canEdit() && !this.invoiceLocked());

    readonly includesAllOccurrences = computed(() => InvoiceForm.isIncludeAllInvoice(this.selectedInvoice()));

    /** Include all is stored as a 1900-2100 span, which is never worth printing as dates. */
    readonly appliedDateRange = computed(() =>
        this.includesAllOccurrences() ? undefined : InvoiceForm.invoiceDateRange(this.selectedInvoice()),
    );

    /** The invoice bills whatever falls in its range, so a hand-picked list has nothing to say. */
    readonly billsByDateRange = computed(() => this.includesAllOccurrences() || !!this.appliedDateRange());

    readonly dateRangeDisplay = computed(() => {
        if (this.includesAllOccurrences()) return "All occurrences included";

        const range = this.appliedDateRange();
        const dateFormat = this.dateFormats()?.date;
        if (!range || !dateFormat) return "";

        const start = S25Datefilter.transform(range.start, dateFormat);
        const end = S25Datefilter.transform(range.end, dateFormat);

        return start === end ? `Date range: ${start}` : `Date range: ${start} – ${end}`;
    });

    readonly editDisabledReason = computed(() => {
        if (this.includesAllOccurrences()) return "This invoice bills every occurrence of this event";
        if (this.appliedDateRange()) return "This invoice bills by date range. Use Edit Invoice to change the dates.";
        return "";
    });

    error = signal("");
    successMsg = signal("");
    editing = signal(false);

    readonly selectedInvoiceOccIds = this.pricingStore.selectedInvoiceOccIds;

    private readonly otherInvoicedIds = computed(() => {
        const selectedInvoiceId = this.selectedInvoice()?.id;
        const eventId = this.eventId();
        const combineRelated = this.combineRelated();
        const otherInvoicedIds = new Set<number>();

        for (const invoice of this.allInvoices()) {
            if (invoice.id === selectedInvoiceId) continue;
            for (const lineItem of invoice.billing?.lineItems ?? []) {
                if (lineItem.itemType === lineItemType.EVENT) continue;
                if (!combineRelated && lineItem.eventId !== eventId) continue;
                for (const occ of lineItem.occurrences ?? []) otherInvoicedIds.add(occ.rsrvId);
            }
        }
        return otherInvoicedIds;
    });

    private readonly availableOccurrences = computed<IncludedOccurrence[]>(() => {
        const otherInvoicedIds = this.otherInvoicedIds();
        const eventIdByRsrvId = this.pricingStore.occurrenceEventIds();
        const includedIds = this.selectedInvoiceOccIds();
        const eventId = this.eventId();
        const combineRelated = this.combineRelated();

        const mapped = Object.values(this.allOccurrences())
            .filter((occ): occ is WSReservations & { rsrvId: number } => S25Util.isDefined(occ.rsrvId))
            .map((occ) => ({ ...occ, eventId: eventIdByRsrvId.get(occ.rsrvId) }));

        return mapped
            .filter((occ) => {
                if (!combineRelated && occ.eventId !== eventId) return false;
                return includedIds.has(occ.rsrvId) || !otherInvoicedIds.has(occ.rsrvId);
            })
            .sort((occA, occB) => new Date(occA.rsrvStartDt!).getTime() - new Date(occB.rsrvStartDt!).getTime());
    });

    private readonly occDataMap = computed<OccDataMap | undefined>(() => {
        const { date: dateFormat, time: timeFormat } = this.dateFormats() ?? {};
        if (!dateFormat || !timeFormat) return undefined;

        const map: OccDataMap = { dateFormat, timeFormat };
        for (const occ of this.availableOccurrences()) {
            map[occ.rsrvId] = { ...occ, occurrence: occ.occurrence ?? "", state: occ.state ?? 0 } as Occurrence;
        }
        return map;
    });

    private readonly formattedOccurrences = computed<IncludedOccurrence[]>(() => {
        const occurrences = this.availableOccurrences().map((occ) => ({ ...occ }));
        const occDataMap = this.occDataMap();
        if (occDataMap) S25PricingUtil.formatOccDates(occurrences as Partial<Occurrence>[], occDataMap);
        return occurrences;
    });

    private readonly selectedInvoiceId = computed(() => this.selectedInvoice()?.id);

    // Seeded from the saved selection and re-seeded when the invoice changes, but never when the selected invoice is
    // merely refetched, so a background invoice refresh cannot discard in-progress changes.
    readonly pendingIncludedIds = linkedSignal<number | undefined, Set<number>>({
        source: this.selectedInvoiceId,
        computation: () => new Set(untracked(this.selectedInvoiceOccIds)),
    });

    readonly displayedOccurrences = computed(() => {
        const occurrences = this.formattedOccurrences();
        if (this.editing()) return occurrences;
        const includedIds = this.selectedInvoiceOccIds();
        return occurrences.filter((occ) => includedIds.has(occ.rsrvId));
    });

    readonly selectedCount = computed(() => this.pendingIncludedIds().size);

    readonly includedCountDisplay = computed(() => {
        const count = this.displayedOccurrences().length;
        return `${count} ${S25Util.pluralize("occurrence", count)}`;
    });

    readonly saveDisabled = computed(() => {
        const pending = this.pendingIncludedIds();
        const saved = this.selectedInvoiceOccIds();
        return pending.size === saved.size && [...pending].every((rsrvId) => saved.has(rsrvId));
    });

    toggleOccurrence(rsrvId: number, included: boolean) {
        this.pendingIncludedIds.update((ids) => {
            const next = new Set(ids);
            if (included) next.add(rsrvId);
            else next.delete(rsrvId);
            return next;
        });
    }

    // Toggling edit mode destroys the control the user just activated, so move focus to whatever replaced it.
    private focusAfterRender(selector: string) {
        afterNextRender(() => this.host.nativeElement.querySelector<HTMLElement>(selector)?.focus(), {
            injector: this.injector,
        });
    }

    startEditing() {
        if (this.billsByDateRange()) return;
        this.pendingIncludedIds.set(new Set(this.selectedInvoiceOccIds()));
        this.editing.set(true);
        this.focusAfterRender(`.io-list input[type="checkbox"]`);
    }

    cancel() {
        this.error.set("");
        this.editing.set(false);
        this.pendingIncludedIds.set(new Set(this.selectedInvoiceOccIds()));
        this.focusAfterRender(".io-edit");
    }

    @Bind
    async save() {
        const invoice = this.selectedInvoice();
        const eventId = this.eventId();
        if (!invoice || !eventId) return;

        this.error.set("");
        const pendingIds = this.pendingIncludedIds();
        const includedOccs = this.formattedOccurrences().filter((occ) => pendingIds.has(occ.rsrvId));
        const rsrvIds = includedOccs.map((occ) => occ.rsrvId);

        const { error } = await this.pricingStore.updateInvoice(
            InvoiceForm.occurrenceListPayload({
                invoice,
                eventIds: [...new Set(includedOccs.map((occ) => occ.eventId ?? eventId))],
                rsrvIds,
            }),
            invoice.id,
        );

        if (error) {
            this.error.set(error);
            return;
        }

        this.editing.set(false);
        this.successMsg.set("Included occurrences updated");
        this.focusAfterRender(".io-edit");
    }
}
